import React, { useState, useEffect } from 'react';
import SecurityUtils from '../utils/SecurityUtils';

// --- INTERFACES (User, Message, Lead) remain the same ---
interface User {
  id: string;
  email: string;
  fullName: string;
  role?: string;
  phone?: string;
  isActive: boolean;
  createdAt: string;
  assignedManagerId?: string;
  assignedManager?: {
    id: string;
    fullName: string;
    email: string;
  };
  _count?: {
    leads?: number;
    managerLeads?: number;
  };
}

interface Message {
  id: string;
  subject: string;
  message: string;
  senderType: 'ADMIN' | 'MANAGER' | 'BOSS';
  senderName: string;
  senderEmail: string;
  createdAt: string;
  isRead: boolean;
  isRepliedTo: boolean;
  repliedAt?: string;
  readAt?: string;
  replies?: Message[];
  admin?: {
    id: string;
    fullName: string;
    email: string;
  };
  manager?: {
    id: string;
    fullName: string;
    email: string;
  };
}

interface Lead {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  loanType: string;
  amount: number;
  status: string;
  notes?: string;
  company?: string;
  location?: string;
  address?: string;
  addressType?: string;
  netSalary?: number;
  grossSalary?: number;
  sideIncome?: number;
  createdAt: string;
  admin: {
    id: string;
    fullName: string;
    email: string;
  };
  existingLoans?: Array<{
    id: string;
    bankName: string;
    amount: number;
    tenure: number;
    emi: number;
    coApplicant?: string;
  }>;
  creditCards?: Array<{
    id: string;
    bankName: string;
    creditLimit: number;
    outstanding: number;
  }>;
  documents?: Array<{
    id: string;
    fileName: string;
    fileUrl?: string;
    fileType?: string;
  }>;
  managerLead?: {
    id: string;
    status: string;
    managerNotes?: string;
    recommendedBank?: string;
    manager: {
      id: string;
      fullName: string;
      email: string;
    };
  };
}
// --- END INTERFACES ---

const BossDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'admins' | 'managers' | 'messages' | 'register'>('overview');
  const [admins, setAdmins] = useState<User[]>([]);
  const [managers, setManagers] = useState<User[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [messageStats, setMessageStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [registerType, setRegisterType] = useState<'admin' | 'manager'>('admin');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [adminToAssign, setAdminToAssign] = useState<User | null>(null);
  const [showLeadDetails, setShowLeadDetails] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showMessageThread, setShowMessageThread] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [messageFilter, setMessageFilter] = useState('all');

  const token = localStorage.getItem('bossToken');
  const BACKEND_URL = 'https://api.askfinancee.com/api'; // Define the backend URL

  const fetchData = async () => {
    setLoading(true);
    try {
      if (!token) return;

      const [adminsRes, managersRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/boss/admins`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/boss/managers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/boss/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const adminsData = await adminsRes.json();
      const managersData = await managersRes.json();
      const statsData = await statsRes.json();

      setAdmins(adminsData.admins || []);
      setManagers(managersData.managers || []);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      if (!token) return;

      const params = new URLSearchParams();
      if (messageFilter !== 'all') params.append('filter', messageFilter);

      const [messagesRes, messageStatsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/boss/messages?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/boss/messages/stats/overview`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const messagesData = await messagesRes.json();
      const statsData = await messageStatsRes.json();

      setMessages(messagesData.messages || []);
      setMessageStats(statsData);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchMessages();
  }, [messageFilter]);

  useEffect(() => {
    const interval = setInterval(fetchMessages, 30000); // Poll messages every 30 seconds
    return () => clearInterval(interval);
  }, [messageFilter]);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
        SecurityUtils.clearAllTokens();
      localStorage.removeItem('bossToken');
      localStorage.removeItem('boss'); // Assuming boss info is stored
      window.location.href = '/'; // Redirect to login or home
    }
  };

  const toggleUserStatus = async (userId: string, type: 'admin' | 'manager', currentStatus: boolean) => {
    try {
      if (!token) return;

      const endpoint = type === 'admin' ? 'admins' : 'managers';
      await fetch(`${BACKEND_URL}/boss/${endpoint}/${userId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ isActive: !currentStatus })
      });

      // Refresh data to reflect the change
      fetchData();
    } catch (error) {
      console.error('Toggle status error:', error);
      // Maybe show an error message to the user
    }
  };

  const handleAssignManager = async (adminId: string, managerId: string) => {
    try {
      if (!token) return;

      const res = await fetch(`${BACKEND_URL}/boss/admins/${adminId}/assign-manager`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ managerId })
      });

      if (!res.ok) throw new Error('Failed to assign manager');

      alert('Manager assigned successfully!');
      setShowAssignModal(false);
      setAdminToAssign(null);
      fetchData(); // Refresh admin list
    } catch (error: any) {
      alert(error.message || 'Failed to assign manager');
    }
  };

  const fetchAdminLeads = async (adminId: string) => {
    try {
      if (!token) return [];

      const res = await fetch(`${BACKEND_URL}/boss/admins/${adminId}/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      return data.leads || [];
    } catch (error) {
      console.error('Failed to fetch admin leads:', error);
      return [];
    }
  };

  const fetchManagerAdmins = async (managerId: string) => {
    try {
      if (!token) return [];

      const res = await fetch(`${BACKEND_URL}/boss/managers/${managerId}/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      return data.admins || [];
    } catch (error) {
      console.error('Failed to fetch manager admins:', error);
      return [];
    }
  };

  const fetchLeadDetails = async (leadId: string) => {
    try {
      if (!token) return;

      const res = await fetch(`${BACKEND_URL}/boss/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      setSelectedLead(data);
      setShowLeadDetails(true);
    } catch (error) {
      console.error('Failed to fetch lead details:', error);
    }
  };

  const handleViewMessage = async (messageId: string) => {
    try {
      if (!token) return;

      const res = await fetch(`${BACKEND_URL}/boss/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      setSelectedMessage(data);
      setShowMessageThread(true);
      // Message will be marked as read on the backend, refresh messages list
      // after modal closes to update UI, handled in modal's onClose
    } catch (error) {
      console.error('Failed to fetch message:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      if (!token) return;

      await fetch(`${BACKEND_URL}/boss/messages/mark-all-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchMessages(); // Refresh messages list
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // --- JSX RENDER ---
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-2xl">👑</div>
              <div>
                <h1 className="text-2xl font-bold">Boss Portal</h1>
                <p className="text-purple-100 text-sm">System Administrator</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setActiveTab('messages')}
                className="relative p-2 text-white hover:bg-white/20 rounded-full transition"
                title="Messages"
              >
                💬
                {messageStats?.unreadMessages > 0 && (
                  <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {messageStats.unreadMessages}
                  </span>
                )}
              </button>
              <button onClick={handleLogout} className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition">
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {['overview', 'admins', 'managers', 'messages', 'register'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`px-6 py-3 font-medium capitalize transition relative ${
                  activeTab === tab ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab === 'register' ? '+ Register User' : tab}
                {tab === 'messages' && messageStats?.unreadMessages > 0 && (
                   <span className="absolute top-2 right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                     {messageStats.unreadMessages}
                   </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading && !stats ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-purple-600"></div>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && stats && (
              <OverviewTab stats={stats} messageStats={messageStats} />
            )}

            {activeTab === 'admins' && (
              <AdminsView
                admins={admins}
                managers={managers}
                onToggleStatus={toggleUserStatus}
                onAssignManager={(admin) => {
                  setAdminToAssign(admin);
                  setShowAssignModal(true);
                }}
                onViewLeads={fetchAdminLeads}
                onViewLeadDetails={fetchLeadDetails}
                setRegisterType={setRegisterType}
                setActiveTab={setActiveTab}
              />
            )}

            {activeTab === 'managers' && (
              <ManagersView
                managers={managers}
                onToggleStatus={toggleUserStatus}
                onViewAdmins={fetchManagerAdmins}
                onViewLeadDetails={fetchLeadDetails} // Pass down the function
                setRegisterType={setRegisterType}
                setActiveTab={setActiveTab}
              />
            )}

            {activeTab === 'messages' && (
              <MessagesView
                messages={messages}
                messageStats={messageStats}
                messageFilter={messageFilter}
                setMessageFilter={setMessageFilter}
                onViewMessage={handleViewMessage}
                onMarkAllRead={handleMarkAllRead}
                onRefresh={fetchMessages}
              />
            )}

            {/* FIX IS HERE: Added BACKEND_URL prop */}
            {activeTab === 'register' && (
              <RegisterUserForm
                type={registerType}
                onSuccess={fetchData}
                BACKEND_URL={BACKEND_URL}
              />
            )}
          </>
        )}
      </main>

      {/* Modals */}
      {showAssignModal && adminToAssign && (
        <AssignManagerModal
          admin={adminToAssign}
          managers={managers}
          onClose={() => {
            setShowAssignModal(false);
            setAdminToAssign(null);
          }}
          onAssign={handleAssignManager}
          BACKEND_URL={BACKEND_URL} // Pass BACKEND_URL
        />
      )}

      {showLeadDetails && selectedLead && (
        <LeadDetailsModal
          lead={selectedLead}
          onClose={() => {
            setShowLeadDetails(false);
            setSelectedLead(null);
          }}
          BACKEND_URL={BACKEND_URL} // Pass BACKEND_URL
        />
      )}

      {showMessageThread && selectedMessage && (
        <MessageThreadModal
          message={selectedMessage}
          onClose={() => {
            setShowMessageThread(false);
            setSelectedMessage(null);
            fetchMessages(); // Refresh messages after closing
          }}
          token={token || ''}
          BACKEND_URL={BACKEND_URL} // Pass BACKEND_URL
        />
      )}
    </div>
  );
};

// --- SUB-COMPONENTS (OverviewTab, MessagesView, etc.) ---
// (These remain unchanged from the previous version)

const OverviewTab: React.FC<{ stats: any; messageStats: any }> = ({ stats, messageStats }) => {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">System Overview</h2>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-lg">
          <div className="text-3xl mb-2">📊</div>
          <div className="text-2xl font-bold">{stats.leads?.total || 0}</div>
          <div className="text-blue-100">Total Leads</div>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white p-6 rounded-xl shadow-lg">
          <div className="text-3xl mb-2">⏳</div>
          <div className="text-2xl font-bold">{stats.leads?.pending || 0}</div>
          <div className="text-yellow-100">Pending</div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-xl shadow-lg">
          <div className="text-3xl mb-2">✅</div>
          <div className="text-2xl font-bold">{stats.leads?.approved || 0}</div>
          <div className="text-green-100">Approved</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-xl shadow-lg">
          <div className="text-3xl mb-2">👥</div>
          <div className="text-2xl font-bold">{stats.admins?.total || 0}</div>
          <div className="text-purple-100">Employees</div>
        </div>
      </div>

      {messageStats && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">📬 Message Center Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{messageStats.totalMessages || 0}</div>
              <div className="text-xs text-gray-600">Total Messages</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{messageStats.unreadMessages || 0}</div>
              <div className="text-xs text-gray-600">Unread</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{messageStats.repliedMessages || 0}</div>
              <div className="text-xs text-gray-600">Replied</div>
            </div>
            <div className="text-center p-4 bg-indigo-50 rounded-lg">
              <div className="text-2xl font-bold text-indigo-600">{messageStats.adminMessages || 0}</div>
              <div className="text-xs text-gray-600">From Admins</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{messageStats.managerMessages || 0}</div>
              <div className="text-xs text-gray-600">From Managers</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MessagesView: React.FC<{
  messages: Message[];
  messageStats: any;
  messageFilter: string;
  setMessageFilter: (filter: string) => void;
  onViewMessage: (id: string) => void;
  onMarkAllRead: () => void;
  onRefresh: () => void;
}> = ({ messages, messageStats, messageFilter, setMessageFilter, onViewMessage, onMarkAllRead, onRefresh }) => {
    return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Message Inbox</h2>
        <div className="flex gap-2">
          <button
            onClick={onRefresh}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
          >
            🔄 Refresh
          </button>
          {messageStats?.unreadMessages > 0 && (
            <button
              onClick={onMarkAllRead}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              ✓ Mark All Read
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setMessageFilter('all')}
            className={`px-4 py-2 rounded-lg transition ${
              messageFilter === 'all' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({messageStats?.totalMessages || 0})
          </button>
          <button
            onClick={() => setMessageFilter('unread')}
            className={`px-4 py-2 rounded-lg transition ${
              messageFilter === 'unread' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Unread ({messageStats?.unreadMessages || 0})
          </button>
          <button
            onClick={() => setMessageFilter('admins')}
            className={`px-4 py-2 rounded-lg transition ${
              messageFilter === 'admins' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            From Admins ({messageStats?.adminMessages || 0})
          </button>
          <button
            onClick={() => setMessageFilter('managers')}
            className={`px-4 py-2 rounded-lg transition ${
              messageFilter === 'managers' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            From Managers ({messageStats?.managerMessages || 0})
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {messages.length > 0 ? (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`border rounded-lg p-4 hover:shadow-md transition cursor-pointer ${
                !msg.isRead ? 'bg-blue-50 border-blue-300 font-semibold' : 'bg-white border-gray-200'
              }`}
              onClick={() => onViewMessage(msg.id)}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      msg.senderType === 'ADMIN' ? 'bg-blue-100 text-blue-700' :
                      msg.senderType === 'MANAGER' ? 'bg-purple-100 text-purple-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {msg.senderType}
                    </span>
                    <span className="font-semibold text-gray-900">{msg.senderName}</span>
                    <span className="text-gray-500 text-sm">({msg.senderEmail})</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">{msg.subject}</h4>
                </div>
                <div className="flex gap-2">
                  {!msg.isRead && (
                    <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                      New
                    </span>
                  )}
                  {msg.isRepliedTo && (
                    <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">
                      ✓ Replied
                    </span>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-2 line-clamp-2">{msg.message}</p>
              <div className="flex justify-between items-center text-xs text-gray-500">
                <span>📅 {new Date(msg.createdAt).toLocaleString()}</span>
                {msg.replies && msg.replies.length > 0 && (
                  <span className="text-green-600">💬 {msg.replies.length} {msg.replies.length === 1 ? 'reply' : 'replies'}</span>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <div className="text-6xl mb-4">📭</div>
            <p className="text-gray-600 text-lg">No messages found</p>
             <p className="text-gray-500 mt-2">
               {messageFilter === 'unread' ? 'All messages have been read' :
                messageFilter === 'admins' ? 'No messages from admins' :
                messageFilter === 'managers' ? 'No messages from managers' :
               'Your inbox is empty'}
             </p>
          </div>
        )}
      </div>
    </div>
  );
};

const MessageThreadModal: React.FC<{
  message: Message;
  onClose: () => void;
  token: string;
  BACKEND_URL: string;
}> = ({ message: initialMessage, onClose, token, BACKEND_URL }) => {
  const [message, setMessage] = useState(initialMessage);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/boss/messages/${message.id}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ message: replyText })
      });

      if (!res.ok) throw new Error('Failed to send reply');

      const updatedRes = await fetch(`${BACKEND_URL}/boss/messages/${message.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedMessage = await updatedRes.json();

      setMessage(updatedMessage);
      setReplyText('');
      alert('Reply sent successfully!');
    } catch (error) {
      console.error('Send reply error:', error);
      alert('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">{message.subject}</h2>
              <p className="text-purple-100 text-sm mt-1">
                From: {message.senderName} ({message.senderType})
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[calc(90vh-300px)] overflow-y-auto">
          {/* Original Message */}
          <div className={`border rounded-lg p-4 mb-4 ${
            message.senderType === 'ADMIN' ? 'bg-blue-50 border-blue-200' :
            message.senderType === 'MANAGER' ? 'bg-purple-50 border-purple-200' :
            'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="flex items-center gap-2 mb-1">
                 <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      message.senderType === 'ADMIN' ? 'bg-blue-100 text-blue-700' :
                      message.senderType === 'MANAGER' ? 'bg-purple-100 text-purple-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {message.senderType}
                    </span>
                  <p className="font-semibold text-gray-900">{message.senderName}</p>
                </div>
                <p className="text-sm text-gray-500">{message.senderEmail}</p>
              </div>
              <div className="text-xs text-gray-500">
                 {new Date(message.createdAt).toLocaleString()}
              </div>
            </div>
            <p className="text-gray-800 whitespace-pre-wrap">{message.message}</p>
          </div>

          {/* Replies */}
          {message.replies && message.replies.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-700 text-sm">Replies ({message.replies.length})</h3>
              {message.replies.map((reply) => (
                <div key={reply.id} className="border rounded-lg p-4 bg-gray-50 border-gray-200 ml-6">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-700">
                          BOSS (You)
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(reply.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <p className="text-gray-800 whitespace-pre-wrap">{reply.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reply Form */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <form onSubmit={handleSendReply}>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your reply..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none resize-none"
              rows={4}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={!replyText.trim() || sending}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition disabled:opacity-50"
              >
                {sending ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const AdminsView: React.FC<{
  admins: User[];
  managers: User[];
  onToggleStatus: (id: string, type: 'admin' | 'manager', status: boolean) => void;
  onAssignManager: (admin: User) => void;
  onViewLeads: (adminId: string) => Promise<Lead[]>;
  onViewLeadDetails: (leadId: string) => void;
  setRegisterType: (type: 'admin' | 'manager') => void;
  setActiveTab: (tab: any) => void;
}> = ({ admins, managers, onToggleStatus, onAssignManager, onViewLeads, onViewLeadDetails, setRegisterType, setActiveTab }) => {
  const [expandedAdmin, setExpandedAdmin] = useState<string | null>(null);
  const [adminLeads, setAdminLeads] = useState<Lead[]>([]);

  const handleViewLeads = async (adminId: string) => {
    if (expandedAdmin === adminId) {
      setExpandedAdmin(null);
      setAdminLeads([]);
    } else {
      const leads = await onViewLeads(adminId);
      setAdminLeads(leads);
      setExpandedAdmin(adminId);
    }
  };
    return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Employees / Agents</h2>
        <button
          onClick={() => { setRegisterType('admin'); setActiveTab('register'); }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          + Add Employee
        </button>
      </div>
      <div className="space-y-4">
        {admins.map((admin) => (
          <div key={admin.id} className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-800">{admin.fullName}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      admin.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {admin.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">📧 Email:</span>
                      <span className="ml-2 text-gray-800">{admin.email}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">👤 Role:</span>
                      <span className="ml-2 text-gray-800">{admin.role}</span>
                    </div>
                     <div>
                       <span className="text-gray-600">📊 Total Leads:</span>
                       <span className="ml-2 text-gray-800 font-semibold">{admin._count?.leads || 0}</span>
                     </div>
                    <div>
                      <span className="text-gray-600">📅 Joined:</span>
                      <span className="ml-2 text-gray-800">{new Date(admin.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {admin.assignedManager ? (
                    <div className="mt-3 bg-purple-50 rounded-lg p-3">
                      <div className="text-sm">
                        <span className="text-purple-700 font-medium">👨‍💼 Assigned Manager:</span>
                        <span className="ml-2 text-gray-800">{admin.assignedManager.fullName}</span>
                        <span className="ml-2 text-gray-500">({admin.assignedManager.email})</span>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 bg-yellow-50 rounded-lg p-3">
                        <div className="text-sm text-yellow-700">
                           ⚠️ No manager assigned
                        </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => onAssignManager(admin)}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition text-sm"
                  >
                    {admin.assignedManager ? 'Change Manager' : 'Assign Manager'}
                  </button>
                   <button
                    onClick={() => handleViewLeads(admin.id)}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition text-sm"
                  >
                     {expandedAdmin === admin.id ? 'Hide Leads' : 'View Leads'}
                  </button>
                  <button
                    onClick={() => onToggleStatus(admin.id, 'admin', admin.isActive)}
                    className="text-indigo-600 hover:text-indigo-800 text-sm font-medium px-3"
                  >
                    {admin.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            </div>

             {expandedAdmin === admin.id && (
              <div className="bg-gray-50 p-6 border-t border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-4">Leads ({adminLeads.length})</h4>
                {adminLeads.length > 0 ? (
                  <div className="space-y-3">
                    {adminLeads.map((lead) => (
                      <div key={lead.id} className="bg-white rounded-lg p-4 shadow-sm">
                          <div className="flex justify-between items-start">
                             <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                   <h5 className="font-semibold text-gray-900">{lead.fullName}</h5>
                                   <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                      lead.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                                      lead.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                      lead.status === 'REVIEW' ? 'bg-blue-100 text-blue-800' :
                                      'bg-yellow-100 text-yellow-800'
                                   }`}>
                                      {lead.status}
                                   </span>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-sm">
                                   <div>
                                      <span className="text-gray-600">📧</span>
                                      <span className="ml-1 text-gray-800">{lead.email}</span>
                                   </div>
                                    <div>
                                      <span className="text-gray-600">🏦</span>
                                      <span className="ml-1 text-gray-800">{lead.loanType}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">💰</span>
                                      <span className="ml-1 text-gray-800 font-semibold">₹{lead.amount.toLocaleString()}</span>
                                    </div>
                                </div>
                                 {lead.managerLead && (
                                    <div className="mt-2 bg-indigo-50 rounded p-2 text-sm">
                                        <span className="text-indigo-700 font-medium">Managed by:</span>
                                        <span className="ml-2 text-gray-800">{lead.managerLead.manager.fullName}</span>
                                        <span className="ml-2 text-indigo-600">({lead.managerLead.status})</span>
                                    </div>
                                 )}
                             </div>
                             <button
                                onClick={() => onViewLeadDetails(lead.id)}
                                className="ml-4 bg-blue-100 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-200 transition text-sm font-medium"
                             >
                                📋 Details
                             </button>
                          </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No leads found</p>
                )}
              </div>
            )}
          </div>
        ))}
        {admins.length === 0 && (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
               <div className="text-6xl mb-4">👥</div>
               <p className="text-gray-600 text-lg">No employees found</p>
            </div>
        )}
      </div>
    </div>
  );
};

const ManagersView: React.FC<{
  managers: User[];
  onToggleStatus: (id: string, type: 'admin' | 'manager', status: boolean) => void;
  onViewAdmins: (managerId: string) => Promise<User[]>;
  onViewLeadDetails: (leadId: string) => void;
  setRegisterType: (type: 'admin' | 'manager') => void;
  setActiveTab: (tab: any) => void;
}> = ({ managers, onToggleStatus, onViewAdmins, setRegisterType, setActiveTab }) => {
  const [expandedManager, setExpandedManager] = useState<string | null>(null);
  const [managerAdmins, setManagerAdmins] = useState<User[]>([]);

  const handleViewAdmins = async (managerId: string) => {
    if (expandedManager === managerId) {
      setExpandedManager(null);
      setManagerAdmins([]);
    } else {
      const admins = await onViewAdmins(managerId);
      setManagerAdmins(admins);
      setExpandedManager(managerId);
    }
  };
    return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Managers</h2>
        <button
          onClick={() => { setRegisterType('manager'); setActiveTab('register'); }}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition"
        >
          + Add Manager
        </button>
      </div>
      <div className="space-y-4">
        {managers.map((manager) => (
          <div key={manager.id} className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-800">{manager.fullName}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      manager.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {manager.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">📧 Email:</span>
                      <span className="ml-2 text-gray-800">{manager.email}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">📱 Phone:</span>
                      <span className="ml-2 text-gray-800">{manager.phone || 'N/A'}</span>
                    </div>
                     <div>
                       <span className="text-gray-600">📊 Total Leads:</span>
                       <span className="ml-2 text-gray-800 font-semibold">{manager._count?.managerLeads || 0}</span>
                     </div>
                    <div>
                      <span className="text-gray-600">📅 Joined:</span>
                      <span className="ml-2 text-gray-800">{new Date(manager.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleViewAdmins(manager.id)}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition text-sm"
                  >
                     {expandedManager === manager.id ? 'Hide Team' : 'View Team'}
                  </button>
                  <button
                    onClick={() => onToggleStatus(manager.id, 'manager', manager.isActive)}
                    className="text-indigo-600 hover:text-indigo-800 text-sm font-medium px-3"
                  >
                    {manager.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            </div>

             {expandedManager === manager.id && (
              <div className="bg-gray-50 p-6 border-t border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-4">Assigned Admins ({managerAdmins.length})</h4>
                 {managerAdmins.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {managerAdmins.map((admin) => (
                           <div key={admin.id} className="bg-white rounded-lg p-4 shadow-sm">
                             <div className="flex items-center gap-2 mb-2">
                                <h5 className="font-semibold text-gray-900">{admin.fullName}</h5>
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  admin.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {admin.isActive ? 'Active' : 'Inactive'}
                                </span>
                             </div>
                             <div className="space-y-1 text-sm">
                                <div>
                                   <span className="text-gray-600">📧</span>
                                   <span className="ml-1 text-gray-800">{admin.email}</span>
                                </div>
                                <div>
                                   <span className="text-gray-600">👤</span>
                                   <span className="ml-1 text-gray-800">{admin.role}</span>
                                </div>
                                 <div>
                                    <span className="text-gray-600">📊</span>
                                    <span className="ml-1 text-gray-800 font-semibold">{admin._count?.leads || 0} leads</span>
                                 </div>
                             </div>
                           </div>
                        ))}
                    </div>
                 ) : (
                    <p className="text-gray-500 text-center py-8">No admins assigned to this manager</p>
                 )}
              </div>
            )}
          </div>
        ))}
         {managers.length === 0 && (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
               <div className="text-6xl mb-4">📊</div>
               <p className="text-gray-600 text-lg">No managers found</p>
            </div>
        )}
      </div>
    </div>
  );
};

const AssignManagerModal: React.FC<{
  admin: User;
  managers: User[];
  onClose: () => void;
  onAssign: (adminId: string, managerId: string) => void;
  BACKEND_URL: string;
}> = ({ admin, managers, onClose, onAssign, BACKEND_URL }) => {
  const [selectedManager, setSelectedManager] = useState(admin.assignedManagerId || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedManager) {
      onAssign(admin.id, selectedManager);
    }
  };
    return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl">
          <h2 className="text-2xl font-bold">Assign Manager</h2>
          <p className="text-purple-100 mt-1">Assign a manager to {admin.fullName}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Manager</label>
            <select
              value={selectedManager}
              onChange={(e) => setSelectedManager(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              required
            >
              <option value="">-- Select a Manager --</option>
              {managers.filter(m => m.isActive).map((manager) => (
                <option key={manager.id} value={manager.id}>
                  {manager.fullName} ({manager.email})
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedManager}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition font-medium disabled:opacity-50"
            >
              Assign Manager
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const LeadDetailsModal: React.FC<{
  lead: Lead;
  onClose: () => void;
  BACKEND_URL: string;
}> = ({ lead, onClose, BACKEND_URL }) => {
  const handleViewDocument = (doc: any) => {
    if (doc.fileUrl) {
      window.open(doc.fileUrl, '_blank');
    } else {
      alert('Document URL not available');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl sticky top-0 z-10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">{lead.fullName}</h2>
              <p className="text-blue-100 text-sm mt-1">{lead.email} • {lead.phone}</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Basic Info */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
               <span>📋</span> Loan Details
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-600">🏦 Loan Type:</span>
                  <span className="ml-2 text-gray-800 font-medium">{lead.loanType}</span>
                </div>
                <div>
                  <span className="text-gray-600">💰 Amount:</span>
                  <span className="ml-2 text-gray-800 font-semibold">₹{lead.amount.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-600">📊 Status:</span>
                   <span className={`ml-2 px-2 py-1 rounded-full text-xs font-semibold ${
                       lead.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                       lead.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                       lead.status === 'REVIEW' ? 'bg-blue-100 text-blue-800' :
                       'bg-yellow-100 text-yellow-800'
                    }`}>
                      {lead.status}
                   </span>
                </div>
                 <div>
                   <span className="text-gray-600">👤 Created By:</span>
                   <span className="ml-2 text-gray-800">{lead.admin.fullName}</span>
                 </div>
                 {lead.notes && (
                    <div className="col-span-2">
                       <span className="text-gray-600">📝 Notes:</span>
                       <div className="ml-2 mt-1 text-gray-800 bg-white p-2 rounded">{lead.notes}</div>
                    </div>
                 )}
             </div>
          </div>

          {/* Personal Info */}
           {(lead.company || lead.location || lead.address) && (
              <div className="bg-purple-50 rounded-lg p-4">
                 <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                    <span>👤</span> Personal Information
                 </h3>
                 <div className="grid grid-cols-2 gap-3 text-sm">
                   {lead.company && (
                     <div>
                       <span className="text-gray-600">🏢 Company:</span>
                       <span className="ml-2 text-gray-800">{lead.company}</span>
                     </div>
                   )}
                   {lead.location && (
                     <div>
                       <span className="text-gray-600">📍 Location:</span>
                       <span className="ml-2 text-gray-800">{lead.location}</span>
                     </div>
                   )}
                   {lead.addressType && (
                     <div>
                       <span className="text-gray-600">🏠 Address Type:</span>
                       <span className="ml-2 text-gray-800">{lead.addressType}</span>
                     </div>
                   )}
                   {lead.address && (
                     <div className="col-span-2">
                       <span className="text-gray-600">📮 Address:</span>
                       <span className="ml-2 text-gray-800">{lead.address}</span>
                     </div>
                   )}
                 </div>
              </div>
           )}

          {/* Income Info */}
           {(lead.netSalary || lead.grossSalary || lead.sideIncome) && (
              <div className="bg-green-50 rounded-lg p-4">
                 <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                    <span>💰</span> Income Information
                 </h3>
                 <div className="grid grid-cols-3 gap-3 text-sm">
                     {lead.netSalary && (
                       <div>
                         <span className="text-gray-600">Net Salary:</span>
                         <div className="font-semibold text-green-700">₹{lead.netSalary.toLocaleString()}</div>
                       </div>
                     )}
                     {lead.grossSalary && (
                       <div>
                         <span className="text-gray-600">Gross Salary:</span>
                         <div className="font-semibold text-green-700">₹{lead.grossSalary.toLocaleString()}</div>
                       </div>
                     )}
                     {lead.sideIncome && (
                       <div>
                         <span className="text-gray-600">Side Income:</span>
                         <div className="font-semibold text-green-700">₹{lead.sideIncome.toLocaleString()}</div>
                       </div>
                     )}
                 </div>
                 <div className="mt-3 pt-3 border-t border-green-200">
                     <span className="text-gray-600">Total Monthly Income:</span>
                     <span className="ml-2 text-lg font-bold text-green-800">
                         ₹{((lead.netSalary || 0) + (lead.sideIncome || 0)).toLocaleString()}
                     </span>
                 </div>
              </div>
           )}

          {/* Existing Loans */}
           {lead.existingLoans && lead.existingLoans.length > 0 && (
             <div className="bg-orange-50 rounded-lg p-4">
               <h3 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
                  <span>🏦</span> Existing Loans ({lead.existingLoans.length})
               </h3>
               <div className="space-y-3">
                  {lead.existingLoans.map((loan, idx) => (
                    <div key={loan.id} className="bg-white rounded-lg p-3 border border-orange-200">
                       <div className="font-medium text-orange-900 mb-2">Loan #{idx + 1} - {loan.bankName}</div>
                       <div className="grid grid-cols-2 gap-2 text-sm">
                           <div>
                             <span className="text-gray-600">Amount:</span>
                             <span className="ml-2 text-gray-800 font-semibold">₹{loan.amount.toLocaleString()}</span>
                           </div>
                           <div>
                             <span className="text-gray-600">EMI:</span>
                             <span className="ml-2 text-gray-800 font-semibold">₹{loan.emi.toLocaleString()}</span>
                           </div>
                           <div>
                             <span className="text-gray-600">Tenure:</span>
                             <span className="ml-2 text-gray-800">{loan.tenure} months</span>
                           </div>
                           {loan.coApplicant && (
                             <div>
                               <span className="text-gray-600">Co-Applicant:</span>
                               <span className="ml-2 text-gray-800">{loan.coApplicant}</span>
                             </div>
                           )}
                       </div>
                    </div>
                  ))}
                   <div className="bg-orange-100 rounded p-2 text-sm font-semibold text-orange-900">
                      Total EMI: ₹{lead.existingLoans.reduce((sum, l) => sum + l.emi, 0).toLocaleString()}/month
                   </div>
               </div>
             </div>
           )}

          {/* Credit Cards */}
           {lead.creditCards && lead.creditCards.length > 0 && (
              <div className="bg-pink-50 rounded-lg p-4">
                 <h3 className="font-semibold text-pink-900 mb-3 flex items-center gap-2">
                    <span>💳</span> Credit Cards ({lead.creditCards.length})
                 </h3>
                 <div className="space-y-3">
                   {lead.creditCards.map((card, idx) => (
                      <div key={card.id} className="bg-white rounded-lg p-3 border border-pink-200">
                         <div className="font-medium text-pink-900 mb-2">Card #{idx + 1} - {card.bankName}</div>
                         <div className="grid grid-cols-2 gap-2 text-sm">
                             <div>
                               <span className="text-gray-600">Credit Limit:</span>
                               <span className="ml-2 text-gray-800 font-semibold">₹{card.creditLimit.toLocaleString()}</span>
                             </div>
                             <div>
                               <span className="text-gray-600">Outstanding:</span>
                               <span className="ml-2 text-gray-800 font-semibold">₹{card.outstanding.toLocaleString()}</span>
                             </div>
                         </div>
                         {/* Utilization Bar */}
                         <div className="mt-2">
                             <div className="flex justify-between text-xs text-gray-600 mb-1">
                                <span>Utilization</span>
                                <span className="font-semibold">{((card.outstanding / card.creditLimit) * 100).toFixed(1)}%</span>
                             </div>
                             <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                   className={`h-2 rounded-full ${
                                      (card.outstanding / card.creditLimit) * 100 > 70 ? 'bg-red-500' :
                                      (card.outstanding / card.creditLimit) * 100 > 50 ? 'bg-yellow-500' : 'bg-green-500'
                                   }`}
                                   style={{ width: `${Math.min((card.outstanding / card.creditLimit) * 100, 100)}%` }}
                                ></div>
                             </div>
                         </div>
                      </div>
                   ))}
                    <div className="bg-pink-100 rounded p-2 text-sm font-semibold text-pink-900">
                      Total Outstanding: ₹{lead.creditCards.reduce((sum, c) => sum + c.outstanding, 0).toLocaleString()}
                   </div>
                 </div>
              </div>
           )}

          {/* Documents */}
           {lead.documents && lead.documents.length > 0 && (
             <div className="bg-indigo-50 rounded-lg p-4">
               <h3 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                  <span>📎</span> Documents ({lead.documents.length})
               </h3>
               <div className="space-y-2">
                  {lead.documents.map((doc) => (
                     <button
                        key={doc.id}
                        onClick={() => handleViewDocument(doc)}
                        className="flex items-center gap-2 text-sm text-indigo-700 hover:text-indigo-900 hover:bg-indigo-100 w-full text-left p-2 rounded transition"
                     >
                        <span className="text-lg">
                           {doc.fileName.endsWith('.pdf') ? '📄' :
                           doc.fileName.match(/\.(jpg|jpeg|png)$/i) ? '🖼️' : '📎'}
                        </span>
                        <span className="flex-1">{doc.fileName}</span>
                         <span className="text-xs text-indigo-500 bg-indigo-100 px-2 py-1 rounded">
                           {doc.fileType?.toUpperCase()}
                         </span>
                     </button>
                  ))}
               </div>
             </div>
           )}

          {/* Manager Review */}
           {lead.managerLead && (
             <div className="bg-gray-100 rounded-lg p-4">
               <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span>📝</span> Manager's Review
               </h3>
               <div className="space-y-2 text-sm">
                   <div>
                     <span className="text-gray-600">👨‍💼 Manager:</span>
                     <span className="ml-2 font-medium text-gray-800">{lead.managerLead.manager.fullName}</span>
                   </div>
                   <div>
                     <span className="text-gray-600">📊 Status:</span>
                     <span className="ml-2 font-medium text-gray-800">{lead.managerLead.status.replace('_', ' ')}</span>
                   </div>
                   {lead.managerLead.recommendedBank && (
                     <div>
                       <span className="text-gray-600">🏦 Recommended Bank:</span>
                       <span className="ml-2 font-medium text-gray-800">{lead.managerLead.recommendedBank}</span>
                     </div>
                   )}
                   {lead.managerLead.managerNotes && (
                     <div>
                       <span className="text-gray-600">📝 Notes:</span>
                       <div className="ml-2 mt-1 text-gray-800 bg-white p-2 rounded">{lead.managerLead.managerNotes}</div>
                     </div>
                   )}
               </div>
             </div>
           )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Updated RegisterUserForm component with password visibility toggle
const RegisterUserForm: React.FC<{
  type: 'admin' | 'manager';
  onSuccess: () => void;
  BACKEND_URL: string;
}> = ({ type, onSuccess, BACKEND_URL }) => {
  const [formData, setFormData] = useState({ fullName: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('bossToken');
      const endpoint = type === 'admin' ? 'admins' : 'managers';

      const res = await fetch(`${BACKEND_URL}/boss/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
         const errorData = await res.json();
         throw new Error(errorData.error || 'Registration failed');
      }

      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} registered successfully!`);
      onSuccess();
      setFormData({ fullName: '', email: '', phone: '', password: '', confirmPassword: '' });
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

    return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        Register New {type === 'admin' ? 'Employee' : 'Manager'}
      </h2>
      <div className="bg-white rounded-xl shadow-md p-8">
         {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600">{error}</div>
         )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              required
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
          {type === 'manager' && (
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
               <input
                 type="tel"
                 value={formData.phone}
                 onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                 className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
               />
             </div>
          )}
          
          {/* Password Field with Eye Icon */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                minLength={8}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  // Eye Slash Icon (hidden state)
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  // Eye Icon (visible state)
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Confirm Password Field with Eye Icon */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? (
                  // Eye Slash Icon (hidden state)
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  // Eye Icon (visible state)
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50"
          >
            {loading ? 'Registering...' : `Register ${type === 'admin' ? 'Employee' : 'Manager'}`}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BossDashboard;